import WMSCapabilities from "ol/format/WMSCapabilities.js";
import { UrlHandler } from "@/services/utils/UrlHandler.js";
import {
  buildScaleWarning,
  classifyScale,
  collectScaleTransitions,
  extractWmsScaleRange,
  normalizeArcGISScaleRange,
  resolveArcGISMetadataEndpoint,
  resolveWmsEndpoint,
  scaleDenominatorForView,
} from "@/services/layers/scaleVisibility.js";

const EVALUATION_DELAY_MS = 30;

class ScaleVisibilityService {
  constructor() {
    this.map = null;
    this.store = null;
    this.unsubscribeStore = null;
    this.moveEndHandler = null;
    this.evaluationTimer = null;
    this.evaluationVersion = 0;
    this.layerStates = new Map();
    this.scaleRangeCache = new Map();
    this.wmsCapabilitiesCache = new Map();
  }

  start(map, store) {
    this.stop();
    if (!map || !store) return;

    this.map = map;
    this.store = store;
    this.moveEndHandler = () => this.scheduleEvaluation();
    this.map.on("moveend", this.moveEndHandler);
    this.unsubscribeStore = this.store.subscribe((mutation) => {
      if (mutation.type === "removeActiveLayer") {
        this.layerStates.delete(String(mutation.payload?.id));
      } else if (mutation.type === "cleanActiveLayers") {
        this.layerStates.clear();
      }

      if (["addActiveLayer", "removeActiveLayer", "cleanActiveLayers"].includes(mutation.type)) {
        this.scheduleEvaluation();
      }
    });
    this.scheduleEvaluation();
  }

  stop() {
    if (this.map && this.moveEndHandler) this.map.un("moveend", this.moveEndHandler);
    if (this.unsubscribeStore) this.unsubscribeStore();
    if (this.evaluationTimer) clearTimeout(this.evaluationTimer);
    this.map = null;
    this.store = null;
    this.unsubscribeStore = null;
    this.moveEndHandler = null;
    this.evaluationTimer = null;
    this.evaluationVersion += 1;
    this.layerStates.clear();
  }

  scheduleEvaluation() {
    if (!this.map || !this.store) return;
    this.evaluationVersion += 1;
    const version = this.evaluationVersion;
    if (this.evaluationTimer) clearTimeout(this.evaluationTimer);
    this.evaluationTimer = setTimeout(() => {
      this.evaluationTimer = null;
      this.evaluate(version);
    }, EVALUATION_DELAY_MS);
  }

  async evaluate(version = this.evaluationVersion) {
    if (!this.map || !this.store) return;
    const activeLayers = [...(this.store.getters.getActiveLayers || [])];
    const evaluated = await Promise.all(activeLayers.map(async (layer) => {
      const range = await this.getScaleRange(layer);
      const provider = layer?.provider?.name;
      const scale = scaleDenominatorForView(this.map?.getView(), provider);
      return { layer, status: classifyScale(range, scale) };
    }));

    // A newer activation or map movement supersedes this asynchronous result.
    if (version !== this.evaluationVersion || !this.map || !this.store) return;

    const activeIds = new Set(activeLayers.map((layer) => String(layer.id)));
    for (const layerId of this.layerStates.keys()) {
      if (!activeIds.has(layerId)) this.layerStates.delete(layerId);
    }

    const { zoomInLayers, zoomOutLayers } = collectScaleTransitions(evaluated, this.layerStates);

    const message = buildScaleWarning(zoomInLayers, zoomOutLayers);
    if (message) this.store.dispatch("toast/show", { message, type: "warning" });
  }

  getScaleRange(layer) {
    const provider = layer?.provider?.name;
    const key = provider === "arcgis"
      ? `arcgis:${resolveArcGISMetadataEndpoint(UrlHandler.getBaseUrl(layer), layer?.codigo)}`
      : `geoserver:${resolveWmsEndpoint(UrlHandler.getBaseUrl(layer))}:${this.getWmsLayerName(layer)}`;

    if (!this.scaleRangeCache.has(key)) {
      const request = provider === "arcgis"
        ? this.loadArcGISScaleRange(layer)
        : provider === "geoserver"
          ? this.loadGeoServerScaleRange(layer)
          : Promise.resolve(null);
      this.scaleRangeCache.set(key, request.catch((error) => {
        console.warn(`[ScaleVisibilityService] No se pudo obtener la escala de ${layer?.name || key}:`, error);
        return null;
      }));
    }
    return this.scaleRangeCache.get(key);
  }

  async loadArcGISScaleRange(layer) {
    const endpoint = resolveArcGISMetadataEndpoint(UrlHandler.getBaseUrl(layer), layer?.codigo);
    if (!endpoint) return null;
    const response = await fetch(`${endpoint}${endpoint.includes("?") ? "&" : "?"}f=json`);
    if (!response.ok) throw new Error(`ArcGIS metadata ${response.status}`);
    const metadata = await response.json();
    if (metadata.error) throw new Error(metadata.error.message || "ArcGIS metadata error");
    return normalizeArcGISScaleRange(metadata);
  }

  getWmsLayerName(layer) {
    const workspace = layer?.provider?.geoserverdata?.workspace || "";
    const datastore = layer?.provider?.geoserverdata?.datastore || "";
    return workspace && datastore ? `${workspace}:${datastore}` : datastore;
  }

  async loadGeoServerScaleRange(layer) {
    const baseUrl = UrlHandler.getBaseUrl(layer);
    const layerName = this.getWmsLayerName(layer);
    if (!baseUrl || !layerName) return null;
    const wmsUrl = resolveWmsEndpoint(baseUrl);

    if (!this.wmsCapabilitiesCache.has(wmsUrl)) {
      const separator = wmsUrl.includes("?") ? "&" : "?";
      const url = `${wmsUrl}${separator}service=WMS&version=1.3.0&request=GetCapabilities`;
      this.wmsCapabilitiesCache.set(wmsUrl, fetch(url).then(async (response) => {
        if (!response.ok) throw new Error(`WMS GetCapabilities ${response.status}`);
        return new WMSCapabilities().read(await response.text());
      }));
    }

    const capabilities = await this.wmsCapabilitiesCache.get(wmsUrl);
    return extractWmsScaleRange(capabilities, layerName);
  }
}

export const scaleVisibilityService = new ScaleVisibilityService();
